import { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, Clock } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { getCacheExpirationStatus, CacheExpirationStatus } from '@/lib/pdv-cache';

interface CacheExpirationAlertProps {
  restaurantId: string;
  isOnline: boolean;
}

export function CacheExpirationAlert({ restaurantId, isOnline }: CacheExpirationAlertProps) {
  const [status, setStatus] = useState<CacheExpirationStatus | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;

    const checkStatus = async () => {
      const cacheStatus = await getCacheExpirationStatus(restaurantId);
      setStatus(cacheStatus);
    };

    checkStatus();
    // Check every 5 minutes
    const interval = setInterval(checkStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [restaurantId]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    // Reload the page to refresh all cached data
    window.location.reload();
  };

  // Only show alert when cache is expiring soon and we're offline
  if (!status || !status.isExpiringSoon || isOnline) {
    return null;
  }

  return (
    <Alert variant="destructive" className="mb-4 border-warning bg-warning/10">
      <AlertTriangle className="h-4 w-4 text-warning" />
      <AlertTitle className="text-warning">Cache expirando</AlertTitle>
      <AlertDescription className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          <span>
            {status.hoursRemaining === 0
              ? 'O cache expira em menos de 1 hora'
              : status.hoursRemaining === 1
              ? 'O cache expira em 1 hora'
              : `O cache expira em ${status.hoursRemaining} horas`}
          </span>
        </div>
        <p className="text-sm">
          Conecte-se à internet para atualizar os dados e continuar operando offline.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="w-fit mt-1"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Atualizar dados
        </Button>
      </AlertDescription>
    </Alert>
  );
}
